<template>
  <v-col cols="12" class="pt-4" style="padding-top: 22px !important">
    <v-card width="100%" height="100%" class="pt-0">
      <v-tabs v-model="activeTab" background-color="white" grow>
        <v-tab>Attributes</v-tab>
        <v-tab>Attribute Profiles</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="pt-4">
        <v-tab-item>
          <v-col class="pt-0">
            <v-row>
              <v-card color="primaryDark" width="100%" dark class="pa-2 subtitle-1">
                <v-row dense>
                  <v-col align-self="center" class="pl-4 pa-0">
                    Attributes
                  </v-col>
                  <v-col cols="2" align-self="end" class="pa-0 pr-1 d-flex justify-end">
                    <v-btn
                      class="ma-0"
                      small
                      dense
                      height="28px"
                      width="175px"
                      :disabled="!CanUserEditAssets()"
                      :color="'#8c8c8c'"
                      @click="attributeName = null; type = null; showAddDialog = true; $nextTick(() => { if ($refs.addForm) $refs.addForm.resetValidation() })"
                    >
                      <span class="pa-2 pl-4">New Attribute</span>
                      <v-icon left light>mdi-plus-circle-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-row>

            <v-row class="ma-2 pa-2 cardContent" justify="center">
              <v-data-table
                id="attributeTable"
                :headers="attributeHeaders"
                :items="filteredAttributes"
                item-key="assetAttributeId"
                :items-per-page="5"
                :search="attributeSearch"
                :expanded.sync="expanded"
                :single-expand="true"
                :sort-by.sync="attributeSortBy"
                :sort-desc.sync="attributeSortDesc"
                :must-sort="false"
                class="elevation-2"
                dense
              >
                <template v-slot:item.sort="">
                  <v-btn icon class="pa-0 ma-0 mr-1 handle">
                    <v-icon size="18px" dark>mdi-drag-horizontal-variant</v-icon>
                  </v-btn>
                </template>

                <template v-slot:top>
                  <v-toolbar flat color="white" height="50px">
                    <v-col cols="6"></v-col>
                    <v-col cols="3" class="py-0 d-flex align-end">
                      <v-select
                        v-model="classificationFilter"
                        :items="classificationFilterOptions"
                        item-text="name"
                        item-value="id"
                        label="Classification"
                        dense
                        single-line
                        hide-details
                        clearable
                        class="my-0"
                      ></v-select>
                    </v-col>
                    <v-col class="py-0 d-flex align-end">
                      <v-text-field
                        v-model="attributeSearch"
                        append-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        class="my-0"
                      ></v-text-field>
                    </v-col>
                  </v-toolbar>
                </template>

                <template v-slot:item.classification="{ item }">
                  <div v-if="canEditClassification(item)" class="d-flex align-center">
                    <v-select
                      :items="classificationOptions"
                      item-text="name"
                      item-value="id"
                      dense
                      hide-details
                      class="classificationSelect"
                      :value="getClassificationValue(item)"
                      :disabled="isClassificationBusy(item)"
                      @change="onClassificationChanged(item, $event)"
                    ></v-select>
                    <v-btn
                      v-if="canClearClassification(item)"
                      icon
                      small
                      class="ml-1"
                      :disabled="isClassificationBusy(item)"
                      title="Reset to business logic default"
                      @click="onClassificationCleared(item)"
                    >
                      <v-icon size="18">mdi-restore</v-icon>
                    </v-btn>
                  </div>
                  <span v-else :title="getClassificationReason(item)">
                    {{ getClassificationLabel(item) }}
                  </span>
                </template>

                <template v-slot:item.action="{ item }">
                  <v-btn
                    icon
                    class="pa-0 ma-0 mr-1"
                    :disabled="!canEditAttributeName(item)"
                    :title="
                      canEditAttributeName(item)
                        ? 'Edit attribute name'
                        : 'Global attributes cannot be renamed'
                    "
                    @click="openEditAttribute(item)"
                  >
                    <v-icon
                      size="18px"
                      dark
                      :color="canEditAttributeName(item) ? '' : 'grey darken-1'"
                      >mdi-pencil</v-icon
                    >
                  </v-btn>
                  <v-btn icon class="pa-0 ma-0">
                    <v-icon
                      size="18px"
                      dark
                      v-on:click="
                        showDeleteDialog = true;
                        attributeToDelete = item;
                      "
                      >mdi-delete</v-icon
                    >
                  </v-btn>
                </template>

                <template v-slot:item.expand="{ item }">
                  <v-btn
                    icon
                    class="pa-0 ma-0"
                    v-if="item.type == 'Multi' && !expanded.includes(item)"
                    v-on:click="expand(item)"
                  >
                    <v-icon size="24px" dark>mdi-chevron-down</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    class="pa-0 ma-0"
                    v-if="item.type == 'Multi' && expanded.includes(item)"
                    v-on:click="closeExpansion()"
                  >
                    <v-icon size="24px" dark>mdi-chevron-up</v-icon>
                  </v-btn>
                </template>

                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pt-2 pa-8">
                    <v-row class="pb-2">
                      <span
                        >The following options are available for this
                        attribute:</span
                      >
                    </v-row>
                    <v-row>
                      <AttributeValues
                        :inputAttribute="item"
                        v-on:BindingUpdated="updatedBinding()"
                      ></AttributeValues>
                    </v-row>
                  </td>
                </template>
              </v-data-table>
            </v-row>
          </v-col>
        </v-tab-item>

        <v-tab-item>
          <v-col class="pt-0">
            <v-row>
              <v-card color="primaryDark" width="100%" dark class="pa-2 subtitle-1">
                <v-row dense>
                  <v-col align-self="center" class="pl-4 pa-0">
                    Attribute Profiles
                  </v-col>
                </v-row>
              </v-card>
            </v-row>

            <v-row class="ma-2 pa-2 cardContent" justify="center">
              <v-data-table
                dense
                :headers="profileAttributeHeaders"
                :items="profileTableItems"
                item-key="key"
                :items-per-page.sync="profileRowsPerPage"
                :page.sync="profileRowsPage"
                :footer-props="{ 'items-per-page-options': profileRowsPerPageOptions }"
                :loading="profileOverridesLoading"
                loading-text="Loading..."
                :sort-by.sync="profileSortBy"
                :sort-desc.sync="profileSortDesc"
                :must-sort="false"
                class="elevation-2"
              >
                <template v-slot:top>
                  <v-toolbar flat color="white" height="50px">
                    <v-col cols="6" class="py-0 d-flex align-end">
                      <v-select
                        v-model="selectedProfileOverrideId"
                        :items="profileSelectionOptions"
                        item-text="name"
                        item-value="id"
                        label="Profile"
                        :loading="profileLoading"
                        :disabled="profileLoading || !effectiveMarketId"
                        dense
                        single-line
                        hide-details
                        clearable
                        class="my-0"
                      ></v-select>
                    </v-col>
                    <v-col class="py-0 d-flex align-end">
                      <v-text-field
                        v-model="profileEditorSearch"
                        append-icon="mdi-magnify"
                        label="Search attributes and values"
                        single-line
                        hide-details
                        class="my-0"
                        :disabled="!selectedProfileOverrideId || !effectiveMarketId"
                      ></v-text-field>
                    </v-col>
                  </v-toolbar>
                </template>

                <template v-slot:no-data>
                  <v-alert type="info" outlined dense class="ma-2 mt-3" v-if="!effectiveMarketId">
                    Select a market before editing profile attribute overrides.
                  </v-alert>
                  <v-alert type="info" outlined dense class="ma-2 mt-3" v-else-if="!selectedProfileOverrideId">
                    Select a profile to edit override names.
                  </v-alert>
                  <span v-else>No attributes found.</span>
                </template>

                <template v-slot:item.marketAttributeValue="{ item: row }">
                  <div :class="row.rowType === 'value' ? 'profileValueLabel' : 'profileAttributeLabel'">
                    <v-btn
                      v-if="row.rowType === 'attribute' && row.hasChildren"
                      icon
                      x-small
                      class="pa-0 ma-0 mr-1 profile-toggle-btn"
                      @click="toggleProfileAttributeExpansion(row.attributeId)"
                    >
                      <v-icon size="16">
                        {{ isProfileAttributeExpanded(row.attributeId) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                    </v-btn>
                    <span
                      v-else-if="row.rowType === 'attribute'"
                      class="profile-toggle-spacer mr-1"
                    ></span>
                    <v-icon v-if="row.rowType === 'value'" size="16" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                    <span>{{ row.displayName }}</span>
                    <span
                      v-if="row.rowType === 'attribute' && row.hasChildren"
                      class="caption grey--text ml-2"
                    >
                      ({{ row.childCount }})
                    </span>
                  </div>
                </template>

                <template v-slot:item.profileValues="{ item: row }">
                  <div v-if="row.isDisabled" class="caption grey--text">
                    <v-icon x-small>mdi-lock</v-icon> Global attribute (not editable)
                  </div>
                  <div v-else class="d-flex align-center gap-2 profile-override__container">
                    <div v-if="!isProfileRowRequired(row)" class="profile-override__input-wrapper">
                      <v-text-field
                        :value="getOverrideDraftValue(row)"
                        @input="setOverrideDraftValue(row, $event)"
                        @keyup.enter="saveOverrideRow(row)"
                        :disabled="isOverrideBusy(row)"
                        dense
                        hide-details
                        placeholder="Enter override name"
                      ></v-text-field>
                    </div>

                    <div v-if="getExistingOverrideValue(row)" class="profile-override__chip-group">
                      <v-chip
                        small
                        outlined
                        color="primary"
                        :loading="isOverrideBusy(row)"
                        :disabled="isOverrideBusy(row)"
                        @click:close="removeOverrideRow(row)"
                        close
                      >
                        {{ getExistingOverrideValue(row) }}
                        <v-icon
                          small
                          class="profile-override__default-icon ml-1"
                          :color="isDefaultOverride(row) ? 'amber darken-2' : 'grey darken-1'"
                          @click.stop="setDefaultOverrideRow(row)"
                        >
                          {{ isDefaultOverride(row) ? "mdi-star" : "mdi-star-outline" }}
                        </v-icon>
                      </v-chip>
                    </div>
                  </div>
                </template>
              </v-data-table>
            </v-row>
          </v-col>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog
      v-model="showDeleteDialog"
      width="500px"
      height="200px"
      v-if="CanUserEditAssets()"
      persistent
    >
      <v-card>
        <v-card-title class="pa-3 headline">Delete Attribute</v-card-title>

        <v-card-text class="cardcontent">
          <v-row class="subtitle-1">
            <span
              >Are you sure you want to delete attribute
              <b>{{
                attributeToDelete != null ? attributeToDelete.name : ""
              }}</b
              >?</span
            >
          </v-row>
          <v-row>
            Deleting this attribute will remove the attribute, all of it's
            values and will remove it from all assets.
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!this.deletingAttribute"
            style="width:100px"
            color="green darken-1"
            dark
            @click="showDeleteDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            style="width:150px"
            color="red darken-1"
            dark
            @click="deleteItem"
          >
            <v-icon v-if="this.deletingAttribute" size="28" color="white" dark>
              mdi-loading mdi-spin
            </v-icon>
            <span v-else>Confirm</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showAddDialog"
      width="350px"
      v-if="CanUserEditAssets()"
      persistent
    >
      <v-card width="100%">
        <v-card-title>
          <span class="pl-4 headline">Add Attribute</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row justify="center">
              <v-form ref="addForm">
                <v-row>
                  <v-col cols="3">
                    <v-subheader>Name:</v-subheader>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="attributeName"
                      single-line
                      dense
                      hide-details
                      required
                      :rules="inputRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="3">
                    <v-subheader>Type:</v-subheader>
                  </v-col>
                  <v-col>
                    <v-select
                      v-model="type"
                      :items="attributeTypes"
                      label="Please Select"
                      item-text="name"
                      item-value="id"
                      dense
                      required
                      :rules="inputRules"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-form>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-row justify="center" style="margin: 0px">
            <v-btn
              style="width:100px"
              :disabled="this.attributeInCreation"
              @click="showAddDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              class="mr-4"
              style="width:100px"
              @click="addAttribute()"
              :loading="this.attributeInCreation"
              :disabled="
                this.attributeInCreation || !CanUserEditAssets() || !valid
              "
            >
              <v-icon
                v-if="this.attributeInCreation"
                size="28"
                color="white"
                dark
              >
                mdi-loading mdi-spin
              </v-icon>
              <span v-else>
                <span>Create</span>
              </span>
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showEditDialog"
      width="350px"
      v-if="CanUserEditAssets()"
      persistent
    >
      <v-card width="100%">
        <v-card-title>
          <span class="pl-4 headline">Edit Attribute</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row justify="center">
              <v-form ref="editForm">
                <v-row>
                  <v-col cols="3">
                    <v-subheader>Name:</v-subheader>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="attributeName"
                      single-line
                      dense
                      hide-details
                      required
                      :rules="inputRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-row justify="center" style="margin: 0px">
            <v-btn
              style="width:100px"
              :disabled="this.updatingAttribute"
              @click="showEditDialog = false; attributeName = null"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              class="mr-4"
              style="width:100px"
              @click="updateAttribute()"
              :loading="this.updatingAttribute"
              :disabled="
                this.updatingAttribute || !canEditAttributeName(attributeToEdit) || !valid
              "
            >
              <v-icon
                v-if="this.updatingAttribute"
                size="28"
                color="white"
                dark
              >
                mdi-loading mdi-spin
              </v-icon>
              <span v-else>
                <span>Update</span>
              </span>
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Sortable from "sortablejs";
import AttributeValues from "../../views/attributeManagement/AttributeValues.vue";

export default {
  name: "Attributes",
  components: {
    AttributeValues,
  },
  props: {
    attributes: {
      default: () => [],
      type: Array,
    },
  },
  mounted() {
    var table = document.querySelector(".v-data-table tbody");
    const _self = this;

    this.loadProfilesForEditor();
    this.ensureAttributeScopeMapLoaded();

    if (!table) {
      return;
    }

    Sortable.create(table, {
      handle: ".handle",
      onStart({}) {
        _self.expanded = [];
      },
      onEnd({ newIndex, oldIndex }) {
        const rowSelected = _self.attributes.splice(oldIndex, 1)[0];
        _self.attributes.splice(newIndex, 0, rowSelected);
        _self.saveOrder();
      },
    });
  },
  computed: {
    effectiveMarketId() {
      return this.$store.getters["assetxpstate/effectiveMarketId"];
    },
    profileSelectionOptions() {
      const selectedMarketId = this.effectiveMarketId;

      return (this.profiles || [])
        .filter((profile) => {
          if (!profile) {
            return false;
          }

          const profileMarketId = profile.marketId || profile.marketid || profile.marketID || "";

          return !selectedMarketId || !profileMarketId || profileMarketId === selectedMarketId;
        })
        .slice()
        .sort((left, right) => {
          const leftName = String((left && left.name) || "").toLowerCase();
          const rightName = String((right && right.name) || "").toLowerCase();

          return leftName.localeCompare(rightName);
        });
    },
    attributeProfileRows() {
      const rows = [];
      (this.attributes || []).forEach((attribute) => {
        const attributeId = this.getAttributeId(attribute);
        if (!attributeId) {
          return;
        }

        // Skip global attributes (isMarketScoped: false)
        const isGlobal = attribute.isMarketScoped === false;
        const childValues = this.isMultiAttribute(attribute)
          ? this.getAttributeValueItems(attribute)
          : [];
        const hasChildren = childValues.length > 0;

        rows.push({
          key: `a-${attributeId}`,
          rowType: "attribute",
          attributeId,
          valueId: null,
          displayName: attribute.name,
          defaultName: attribute.name,
          hasChildren,
          childCount: childValues.length,
          isGlobal,
          isDisabled: isGlobal,
        });

        if (hasChildren) {
          childValues.forEach((value) => {
            if (!value) {
              return;
            }

            rows.push({
              key: `v-${attributeId}-${value.id}`,
              rowType: "value",
              attributeId,
              valueId: value.id,
              displayName: `${attribute.name} / ${value.name}`,
              defaultName: value.name,
              isGlobal,
              isDisabled: isGlobal,
            });
          });
        }
      });

      return rows;
    },
    filteredAttributeProfileRows() {
      const term = (this.profileEditorSearch || "").trim().toLowerCase();
      const rows = this.attributeProfileRows;

      if (!term) {
        return rows.filter((row) => {
          if (row.rowType === "attribute") {
            return true;
          }

          return this.isProfileAttributeExpanded(row.attributeId);
        });
      }

      const matchingAttributeIds = new Set();

      rows.forEach((row) => {
        const matches =
          (row.displayName || "").toLowerCase().includes(term) ||
          (row.defaultName || "").toLowerCase().includes(term);

        if (matches) {
          matchingAttributeIds.add(row.attributeId);
        }
      });

      return rows.filter((row) => {
        if (!matchingAttributeIds.has(row.attributeId)) {
          return false;
        }

        if (row.rowType === "attribute") {
          return true;
        }

        return (
          (row.displayName || "").toLowerCase().includes(term) ||
          (row.defaultName || "").toLowerCase().includes(term) ||
          this.isProfileAttributeExpanded(row.attributeId)
        );
      });
    },
    profileTableItems() {
      if (!this.effectiveMarketId || !this.selectedProfileOverrideId) {
        return [];
      }
      return this.filteredAttributeProfileRows;
    },
    filteredAttributes() {
      const resolveFilterMode = (value) => {
        if (value === null || value === undefined || value === "") {
          return "all";
        }

        if (typeof value === "string") {
          const lower = value.trim().toLowerCase();
          if (lower === "all" || lower === "required" || lower === "core" || lower === "optional") {
            return lower;
          }
        }

        if (typeof value === "number") {
          if (value === 0) {
            return "required";
          }
          if (value === 1) {
            return "core";
          }
          if (value === 2) {
            return "optional";
          }
        }

        if (typeof value === "object") {
          if (value.id !== undefined) {
            return resolveFilterMode(value.id);
          }

          if (value.name !== undefined) {
            return resolveFilterMode(value.name);
          }
        }

        return "all";
      };

      const resolveClassificationValue = (value) => {
        if (value === null || value === undefined || value === "") {
          return null;
        }

        if (typeof value === "number") {
          return value;
        }

        if (typeof value === "string") {
          const trimmed = value.trim();
          const numeric = parseInt(trimmed, 10);
          if (!Number.isNaN(numeric)) {
            return numeric;
          }

          const lower = trimmed.toLowerCase();
          if (lower === "required") {
            return 0;
          }
          if (lower === "core") {
            return 1;
          }
          if (lower === "optional") {
            return 2;
          }

          return null;
        }

        if (typeof value === "object" && value.id !== undefined) {
          return resolveClassificationValue(value.id);
        }

        return null;
      };

      const selectedFilter = resolveFilterMode(this.classificationFilter);
      if (selectedFilter === "all") {
        return this.attributes;
      }

      return this.attributes.filter((attribute) => {
        const value = resolveClassificationValue(this.getClassificationValue(attribute));

        if (selectedFilter === "optional") {
          return value !== 0 && value !== 1;
        }
        if (selectedFilter === "required") {
          return value === 0;
        }
        if (selectedFilter === "core") {
          return value === 1;
        }

        return true;
      });
    },
  },
  data: () => ({
    orderingAttributes: false,
    attributeSortBy: [],
    attributeSortDesc: [],
    profileSortBy: [],
    profileSortDesc: [],
    activeTab: 0,
    showAddDialog: false,
    showEditDialog: false,
    showDeleteDialog: false,
    attributeToDelete: {},
    attributeToEdit: {},
    updatingAttribute: false,
    deletingAttribute: false,
    attributeSearch: "",
    expanded: [],
    attributeHeaders: [
      {
        text: "",
        value: "sort",
        sortable: false,
        align: "center",
        width: "5%",
      },
      {
        text: "Attribute",
        value: "name",
        sortable: true,
        align: "start",
        width: "30%",
      },
      {
        text: "Type",
        value: "type",
        sortable: true,
        align: "start",
        width: "20%",
      },
      {
        text: "Classification",
        value: "classification",
        sortable: false,
        align: "start",
        width: "20%",
      },
      {
        text: "",
        value: "action",
        sortable: false,
        align: "center",
        width: "10%",
      },
      {
        text: "",
        value: "expand",
        sortable: false,
        align: "center",
        width: "5%",
      },
    ],
    profileAttributeHeaders: [
      {
        text: "Market Attributes & Values",
        value: "marketAttributeValue",
        sortable: false,
        width: "42%",
      },
      {
        text: "Profile Values",
        value: "profileValues",
        sortable: false,
        width: "58%",
      },
    ],

    valid: true,
    inputRules: [
      (v) => v !== null || "This is a required field.",
      (v) => v !== "" || "This is a required field.",
    ],
    attributeTypes: [
      { name: "Free text", value: "STRING", id: 1 },
      { name: "Numeric", value: "NUMERIC", id: 2 },
      { name: "Multiple Choice", value: "ASSETCATEGORY", id: 3 },
    ],
    classificationOptions: [
      { name: "Required", id: 0 },
      { name: "Core", id: 1 },
      { name: "Optional", id: 2 },
    ],
    classificationFilterOptions: [
      { name: "All", id: "all" },
      { name: "Required", id: "required" },
      { name: "Core", id: "core" },
      { name: "Optional", id: "optional" },
    ],
    classificationFilter: "all",
    classificationsByAttributeId: {},
    classificationBusyByAttributeId: {},
    profileLoading: false,
    profileOverridesLoading: false,
    profiles: [],
    selectedProfileOverrideId: null,
    profileOverridesByAttributeId: {},
    expandedProfileAttributeIds: {},
    attributeValueItemsByAttributeId: {},
    loadingAttributeValueItemsByAttributeId: {},
    overrideDraftsByKey: {},
    overrideBusyByKey: {},
    profileEditorSearch: "",
    profileRowsPage: 1,
    profileRowsPerPage: 5,
    profileRowsPerPageOptions: [5, 10, 25, 50],
    attributeNameEditabilityById: {},
    loadingAttributeNameEditabilityById: {},
    attributeScopeById: {},
    attributeScopeLoaded: false,
    loadingAttributeScope: false,
    attributeInCreation: false,
    attributeName: null,
    type: null,
  }),
  methods: {
    ...mapGetters("authentication", ["CanUserEditAssets"]),
    ...mapActions("assetxpstate", [
      "RemoveAssetAttribute",
      "RemoveCategoryAndAssets",
      "CreateAttribute",
      "RenameAttribute",
      "OrderAttributes",
      "GetAttributeClassifications",
      "GetAttributeClassificationsByMarket",
      "SetAttributeClassification",
      "DeleteAttributeClassification",
      "GetAllAttributes",
      "GetAttributeDetails",
      "GetProfiles",
      "GetProfileAttributeOverrides",
      "SetProfileAttributeNameOverride",
      "RemoveProfileAttributeNameOverride",
      "SetProfileAttributeValueNameOverride",
      "RemoveProfileAttributeValueNameOverride",
    ]),

    getAttributeId(item) {
      if (!item) {
        return null;
      }

      return item.assetAttributeId || item.id || item.attributeId || null;
    },
    normalizeScopeEntry(item) {
      const attributeId = this.getAttributeId(item);
      if (!attributeId) {
        return null;
      }

      const isGlobalValue = item ? (item.isGlobal ?? item.IsGlobal) : undefined;
      const isMarketScopedValue = item ? item.isMarketScoped : undefined;
      const marketIdValue = this.getAttributeMarketId(item);
      const hasMarketSignal =
        this.hasAttributeMarketIdField(item) ||
        isMarketScopedValue !== undefined ||
        isGlobalValue !== undefined;

      return {
        attributeId,
        isGlobal: isGlobalValue,
        isMarketScoped: isMarketScopedValue,
        marketId: marketIdValue,
        hasMarketSignal,
      };
    },
    mergeScopeEntries(items) {
      const rows = Array.isArray(items) ? items : [];
      const nextMap = { ...this.attributeScopeById };

      rows.forEach((item) => {
        const entry = this.normalizeScopeEntry(item);
        if (!entry) {
          return;
        }

        nextMap[entry.attributeId] = entry;
      });

      this.attributeScopeById = nextMap;
    },
    async ensureAttributeScopeMapLoaded() {
      if (this.attributeScopeLoaded || this.loadingAttributeScope) {
        return;
      }

      this.loadingAttributeScope = true;
      try {
        const allAttributes = await this.GetAllAttributes();
        this.mergeScopeEntries(allAttributes);
        this.attributeScopeLoaded = true;
      } catch (error) {
        this.attributeScopeLoaded = false;
      } finally {
        this.loadingAttributeScope = false;
      }
    },
    deriveGlobalFromScopeEntry(scopeEntry) {
      if (!scopeEntry) {
        return null;
      }

      if (this.isTrueLike(scopeEntry.isGlobal)) {
        return true;
      }

      if (this.isFalseLike(scopeEntry.isGlobal)) {
        return false;
      }

      if (this.isFalseLike(scopeEntry.isMarketScoped)) {
        return true;
      }

      if (this.isTrueLike(scopeEntry.isMarketScoped)) {
        return false;
      }

      if (scopeEntry.hasMarketSignal) {
        return this.isNullOrBlank(scopeEntry.marketId);
      }

      return null;
    },
    getAttributeMarketId(item) {
      if (!item) {
        return null;
      }

      const directMarketId =
        item.marketId ??
        item.MarketId ??
        item.marketID ??
        item.marketid ??
        item.attributeMarketId ??
        item.AttributeMarketId ??
        item.market ??
        null;

      if (
        directMarketId &&
        typeof directMarketId === "object" &&
        !Array.isArray(directMarketId)
      ) {
        return (
          directMarketId.id ??
          directMarketId.marketId ??
          directMarketId.MarketId ??
          directMarketId.marketID ??
          directMarketId.marketid ??
          null
        );
      }

      return directMarketId;
    },
    isFalseLike(value) {
      if (value === false || value === 0) {
        return true;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        return normalized === "false" || normalized === "0" || normalized === "no";
      }

      return false;
    },
    isTrueLike(value) {
      if (value === true || value === 1) {
        return true;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        return normalized === "true" || normalized === "1" || normalized === "yes";
      }

      return false;
    },
    hasAttributeMarketIdField(item) {
      if (!item) {
        return false;
      }

      const hasDirectField =
        Object.prototype.hasOwnProperty.call(item, "marketId") ||
        Object.prototype.hasOwnProperty.call(item, "MarketId") ||
        Object.prototype.hasOwnProperty.call(item, "marketID") ||
        Object.prototype.hasOwnProperty.call(item, "marketid") ||
        Object.prototype.hasOwnProperty.call(item, "attributeMarketId") ||
        Object.prototype.hasOwnProperty.call(item, "AttributeMarketId") ||
        Object.prototype.hasOwnProperty.call(item, "market");

      if (hasDirectField) {
        return true;
      }

      const marketObject = item.market;
      if (!marketObject || typeof marketObject !== "object" || Array.isArray(marketObject)) {
        return false;
      }

      return (
        Object.prototype.hasOwnProperty.call(marketObject, "id") ||
        Object.prototype.hasOwnProperty.call(marketObject, "marketId") ||
        Object.prototype.hasOwnProperty.call(marketObject, "MarketId") ||
        Object.prototype.hasOwnProperty.call(marketObject, "marketID") ||
        Object.prototype.hasOwnProperty.call(marketObject, "marketid")
      );
    },
    isGlobalAttribute(item) {
      if (!item) {
        return false;
      }

      if (this.isFalseLike(item.isMarketScoped)) {
        return true;
      }

      if (this.isTrueLike(item.isMarketScoped)) {
        return false;
      }

      // Only use null/empty market id semantics when the payload explicitly
      // includes a market id field; missing field should not be treated as global.
      if (!this.hasAttributeMarketIdField(item)) {
        return false;
      }

      const marketId = this.getAttributeMarketId(item);

      if (marketId === null || marketId === undefined) {
        return true;
      }

      if (typeof marketId === "string") {
        return marketId.trim() === "";
      }

      return false;
    },
    extractMarketIdCandidate(source) {
      if (!source || typeof source !== "object") {
        return null;
      }

      const direct =
        source.marketId ??
        source.MarketId ??
        source.marketID ??
        source.marketid ??
        source.attributeMarketId ??
        source.AttributeMarketId ??
        source.market ??
        null;

      if (direct && typeof direct === "object" && !Array.isArray(direct)) {
        return (
          direct.id ??
          direct.marketId ??
          direct.MarketId ??
          direct.marketID ??
          direct.marketid ??
          null
        );
      }

      return direct;
    },
    isNullOrBlank(value) {
      return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
    },
    isGlobalAttributeFromDetails(details, fallbackItem) {
      if (details && this.isFalseLike(details.isMarketScoped)) {
        return true;
      }
      if (details && this.isTrueLike(details.isMarketScoped)) {
        return false;
      }

      const marketIdFromDetails = this.extractMarketIdCandidate(details);
      if (!this.isNullOrBlank(marketIdFromDetails)) {
        return false;
      }

      if (details && Object.prototype.hasOwnProperty.call(details, "marketId")) {
        return true;
      }
      if (details && Object.prototype.hasOwnProperty.call(details, "MarketId")) {
        return true;
      }
      if (details && Object.prototype.hasOwnProperty.call(details, "marketID")) {
        return true;
      }
      if (details && Object.prototype.hasOwnProperty.call(details, "marketid")) {
        return true;
      }

      return this.isGlobalAttribute(fallbackItem);
    },
    getCachedAttributeNameEditability(item) {
      const attributeId = this.getAttributeId(item);
      if (!attributeId) {
        return null;
      }

      if (!Object.prototype.hasOwnProperty.call(this.attributeNameEditabilityById, attributeId)) {
        return null;
      }

      return !!this.attributeNameEditabilityById[attributeId];
    },
    async resolveAttributeNameEditability(item) {
      if (!this.CanUserEditAssets()) {
        return false;
      }

      const attributeId = this.getAttributeId(item);
      if (!attributeId) {
        return false;
      }

      const cached = this.getCachedAttributeNameEditability(item);
      if (cached !== null) {
        return cached;
      }

      const fromCurrentRows = this.normalizeScopeEntry(item);
      const fromCurrentRowsGlobal = this.deriveGlobalFromScopeEntry(fromCurrentRows);
      if (fromCurrentRowsGlobal !== null) {
        const canEditFromCurrentRows = !fromCurrentRowsGlobal;
        this.$set(this.attributeNameEditabilityById, attributeId, canEditFromCurrentRows);
        return canEditFromCurrentRows;
      }

      await this.ensureAttributeScopeMapLoaded();

      const scopeEntry = this.attributeScopeById[attributeId] || null;
      const globalFromScopeMap = this.deriveGlobalFromScopeEntry(scopeEntry);
      if (globalFromScopeMap !== null) {
        const canEditFromScopeMap = !globalFromScopeMap;
        this.$set(this.attributeNameEditabilityById, attributeId, canEditFromScopeMap);
        return canEditFromScopeMap;
      }

      if (this.loadingAttributeNameEditabilityById[attributeId]) {
        return true;
      }

      this.$set(this.loadingAttributeNameEditabilityById, attributeId, true);

      try {
        const response = await this.GetAttributeDetails(attributeId);
        const details = response && response.data ? response.data : null;
        const isGlobal = this.isGlobalAttributeFromDetails(details, item);
        const canEdit = !isGlobal;
        this.$set(this.attributeNameEditabilityById, attributeId, canEdit);
        return canEdit;
      } catch (error) {
        // Fail closed to prevent accidental edits when scope cannot be determined.
        this.$set(this.attributeNameEditabilityById, attributeId, false);
        return false;
      } finally {
        this.$set(this.loadingAttributeNameEditabilityById, attributeId, false);
      }
    },
    canEditAttributeName(item) {
      if (!this.CanUserEditAssets()) {
        return false;
      }

      const cached = this.getCachedAttributeNameEditability(item);
      if (cached !== null) {
        return cached;
      }

      // Check backend-provided isGlobal flag directly (respects it immediately)
      if (item) {
        const isGlobalFromBackend = item.isGlobal ?? item.IsGlobal;
        if (isGlobalFromBackend !== undefined) {
          return !isGlobalFromBackend; // editable only if NOT global
        }
      }

      // Unknown until details resolve; enforced in open/update guards.
      return true;
    },
    async openEditAttribute(item) {
      const canEdit = await this.resolveAttributeNameEditability(item);
      if (!canEdit) {
        return;
      }

      this.showEditDialog = true;
      this.attributeToEdit = item;
      this.attributeName = item.name;
    },
    getClassificationEntry(item) {
      const attributeId = this.getAttributeId(item);
      if (!attributeId) {
        return null;
      }

      return this.classificationsByAttributeId[attributeId] || null;
    },
    getClassificationValue(item) {
      const entry = this.getClassificationEntry(item);
      return entry ? entry.classification : null;
    },
    getClassificationReason(item) {
      const entry = this.getClassificationEntry(item);
      return entry && entry.nonEditableReason ? entry.nonEditableReason : "";
    },
    getClassificationLabel(item) {
      const classificationValue = this.getClassificationValue(item);

      if (classificationValue === 0) {
        return "Required";
      }
      if (classificationValue === 1) {
        return "Core";
      }
      if (classificationValue === 2) {
        return "Optional";
      }

      return "-";
    },
    isProfileRowRequired(row) {
      if (!row || !row.attributeId) {
        return false;
      }

      const entry = this.classificationsByAttributeId[row.attributeId];
      return !!entry && entry.classification === 0;
    },
    isClassificationBusy(item) {
      const attributeId = this.getAttributeId(item);
      return !!this.classificationBusyByAttributeId[attributeId];
    },
    canEditClassification(item) {
      const entry = this.getClassificationEntry(item);
      return !!(entry && entry.editable && this.CanUserEditAssets());
    },
    canClearClassification(item) {
      const entry = this.getClassificationEntry(item);
      return !!(entry && entry.fromDatabase && entry.editable && this.CanUserEditAssets());
    },
    refreshClassificationEntry(attributeId) {
      if (!attributeId) {
        return Promise.resolve();
      }

      return this.GetAttributeClassifications([attributeId]).then((response) => {
        const rows = response && Array.isArray(response.data) ? response.data : [];
        const row = rows.find((r) => r && r.attributeId === attributeId);

        if (!row) {
          this.$delete(this.classificationsByAttributeId, attributeId);
          return;
        }

        this.$set(this.classificationsByAttributeId, attributeId, {
          ...row,
          classification:
            typeof row.classification === "string"
              ? parseInt(row.classification, 10)
              : row.classification,
        });
      });
    },
    loadAttributeClassifications() {
      const attributeIds = this.attributes
        .map((attribute) => this.getAttributeId(attribute))
        .filter((id) => !!id);

      if (attributeIds.length === 0) {
        this.classificationsByAttributeId = {};
        return;
      }

      const mapFromResponse = (responseData) => {
        const classificationRows = Array.isArray(responseData)
          ? responseData
          : [];

        const validIds = new Set(attributeIds);
        const nextMap = {};
        classificationRows.forEach((row) => {
          if (!row || !row.attributeId || !validIds.has(row.attributeId)) {
            return;
          }

          nextMap[row.attributeId] = {
            ...row,
            classification:
              typeof row.classification === "string"
                ? parseInt(row.classification, 10)
                : row.classification,
          };
        });

        this.classificationsByAttributeId = nextMap;
      };

      this.GetAttributeClassificationsByMarket()
        .then((response) => {
          const responseData = response && response.data ? response.data : [];
          if (Array.isArray(responseData) && responseData.length > 0) {
            mapFromResponse(responseData);
            return;
          }

          return this.GetAttributeClassifications(attributeIds).then((fallbackResponse) => {
            const fallbackData = fallbackResponse && fallbackResponse.data
              ? fallbackResponse.data
              : [];
            mapFromResponse(fallbackData);
          });
        })
        .catch(() => {
          this.GetAttributeClassifications(attributeIds).then((fallbackResponse) => {
            const fallbackData = fallbackResponse && fallbackResponse.data
              ? fallbackResponse.data
              : [];
            mapFromResponse(fallbackData);
          });
        });
    },
    onClassificationChanged(item, selectedValue) {
      const entry = this.getClassificationEntry(item);
      const attributeId = this.getAttributeId(item);
      if (!entry || !attributeId) {
        return;
      }

      const normalizedValue =
        typeof selectedValue === "string"
          ? parseInt(selectedValue, 10)
          : selectedValue;

      if (entry.classification === normalizedValue) {
        return;
      }

      const previousValue = entry.classification;
      this.$set(this.classificationBusyByAttributeId, attributeId, true);

      this.SetAttributeClassification({
        attributeId,
        classification: normalizedValue,
      })
        .then(() => {
          entry.classification = normalizedValue;
          entry.fromDatabase = true;
          entry.nonEditableReason = null;

          return this.refreshClassificationEntry(attributeId);
        })
        .catch(() => {
          entry.classification = previousValue;
        })
        .finally(() => {
          this.$set(this.classificationBusyByAttributeId, attributeId, false);
        });
    },
    onClassificationCleared(item) {
      const entry = this.getClassificationEntry(item);
      const attributeId = this.getAttributeId(item);

      if (!entry || !attributeId || !entry.fromDatabase) {
        return;
      }

      this.$set(this.classificationBusyByAttributeId, attributeId, true);

      this.DeleteAttributeClassification(attributeId)
        .then(() => {
          return this.refreshClassificationEntry(attributeId);
        })
        .finally(() => {
          this.$set(this.classificationBusyByAttributeId, attributeId, false);
        });
    },
    isMultiAttribute(attribute) {
      if (!attribute) {
        return false;
      }

      const typeText = String(attribute.type || attribute.attributeTypeName || "")
        .trim()
        .toLowerCase();
      const numericType = Number(attribute.attributeType);

      return (
        typeText === "multi" ||
        typeText === "assetcategory" ||
        numericType === 0 ||
        numericType === 3
      );
    },
    normalizeAttributeValueItems(rawItems) {
      const items = Array.isArray(rawItems) ? rawItems : [];
      const seen = new Set();

      return items
        .map((item) => {
          const valueId = item
            ? item.id ||
              item.valueId ||
              item.assetCategoryId ||
              item.assetDetailId ||
              item.assetListingId
            : null;
          const valueName = item
            ? item.name || item.value || item.label || item.displayName || item.title
            : null;

          if (!valueId || !valueName) {
            return null;
          }

          const key = `${valueId}`;
          if (seen.has(key)) {
            return null;
          }
          seen.add(key);

          return { id: valueId, name: valueName };
        })
        .filter((item) => !!item);
    },
    getAttributeValueItems(attribute) {
      const rawSources = attribute
        ? [
            attribute.multiChoiceItems,
            attribute.values,
            attribute.attributeValues,
            attribute.multiChoiceValues,
            attribute.items,
          ]
        : [];

      for (let i = 0; i < rawSources.length; i += 1) {
        const normalized = this.normalizeAttributeValueItems(rawSources[i]);
        if (normalized.length > 0) {
          return normalized;
        }
      }

      const attributeId = this.getAttributeId(attribute);
      if (!attributeId) {
        return [];
      }

      return this.attributeValueItemsByAttributeId[attributeId] || [];
    },
    loadMissingMultiAttributeValues() {
      const attributes = Array.isArray(this.attributes) ? this.attributes : [];

      attributes.forEach((attribute) => {
        if (!this.isMultiAttribute(attribute)) {
          return;
        }

        const attributeId = this.getAttributeId(attribute);
        if (!attributeId) {
          return;
        }

        if (this.getAttributeValueItems(attribute).length > 0) {
          return;
        }

        if (
          this.loadingAttributeValueItemsByAttributeId[attributeId] ||
          this.attributeValueItemsByAttributeId[attributeId]
        ) {
          return;
        }

        this.$set(this.loadingAttributeValueItemsByAttributeId, attributeId, true);

        this.GetAttributeDetails(attributeId)
          .then((result) => {
            const details = result && result.data ? result.data : null;
            const normalized = this.normalizeAttributeValueItems(
              details
                ? details.values ||
                    details.multiChoiceItems ||
                    details.attributeValues ||
                    details.multiChoiceValues ||
                    details.items
                : []
            );

            this.$set(this.attributeValueItemsByAttributeId, attributeId, normalized);
          })
          .finally(() => {
            this.$set(this.loadingAttributeValueItemsByAttributeId, attributeId, false);
          });
      });
    },
    getOverrideMapForAttribute(attributeId) {
      if (!attributeId) {
        return null;
      }

      return this.profileOverridesByAttributeId[attributeId] || null;
    },
    getExistingOverrideValue(row) {
      const attributeOverride = this.getOverrideMapForAttribute(row.attributeId);
      if (!attributeOverride) {
        return "";
      }

      if (row.rowType === "attribute") {
        return attributeOverride.attributeNameOverride || "";
      }

      const valueOverrides = attributeOverride.valueNameOverrides || {};
      return valueOverrides[row.valueId] || "";
    },
    getOverrideRowKey(row) {
      return row && row.key ? row.key : "";
    },
    getOverrideDraftValue(row) {
      const rowKey = this.getOverrideRowKey(row);
      if (!rowKey) {
        return "";
      }

      // Only return the draft value; do not fall back to existing override
      // (existing override is shown in the chip)
      return this.overrideDraftsByKey[rowKey] || "";
    },
    setOverrideDraftValue(row, value) {
      const rowKey = this.getOverrideRowKey(row);
      if (!rowKey) {
        return;
      }

      this.$set(this.overrideDraftsByKey, rowKey, value);
    },
    isOverrideBusy(row) {
      const rowKey = this.getOverrideRowKey(row);
      return !!this.overrideBusyByKey[rowKey];
    },
    isProfileAttributeExpanded(attributeId) {
      return !!this.expandedProfileAttributeIds[attributeId];
    },
    toggleProfileAttributeExpansion(attributeId) {
      this.$set(
        this.expandedProfileAttributeIds,
        attributeId,
        !this.isProfileAttributeExpanded(attributeId)
      );
    },
    isDefaultOverride(row) {
      // For now, there is only one override per row
      // If we add support for multiple overrides, we'd check a default flag
      return !!this.getExistingOverrideValue(row);
    },
    loadProfilesForEditor() {
      this.profileLoading = true;

      this.GetProfiles()
        .then((profiles) => {
          this.profiles = Array.isArray(profiles) ? profiles : [];
          if (!this.selectedProfileOverrideId && this.profileSelectionOptions.length > 0) {
            this.selectedProfileOverrideId = this.profileSelectionOptions[0].id;
          }
        })
        .finally(() => {
          this.profileLoading = false;
        });
    },
    loadProfileOverrides() {
      if (!this.selectedProfileOverrideId || !this.effectiveMarketId) {
        this.profileOverridesByAttributeId = {};
        this.overrideDraftsByKey = {};
        return;
      }

      this.profileOverridesLoading = true;
      this.GetProfileAttributeOverrides({
        profileId: this.selectedProfileOverrideId,
        marketId: this.effectiveMarketId,
      })
        .then((response) => {
          const rows = response && Array.isArray(response.data) ? response.data : [];
          const nextMap = {};

          rows.forEach((row) => {
            if (!row || !row.attributeId) {
              return;
            }

            const valueOverrides = {};
            const values = Array.isArray(row.valueNameOverrides) ? row.valueNameOverrides : [];
            values.forEach((valueRow) => {
              if (!valueRow || !valueRow.valueId) {
                return;
              }

              valueOverrides[valueRow.valueId] = valueRow.name || "";
            });

            nextMap[row.attributeId] = {
              attributeNameOverride: row.attributeNameOverride || "",
              valueNameOverrides: valueOverrides,
            };
          });

          this.profileOverridesByAttributeId = nextMap;
          this.overrideDraftsByKey = {};
        })
        .finally(() => {
          this.profileOverridesLoading = false;
        });
    },
    saveOverrideRow(row) {
      const nextValue = (this.getOverrideDraftValue(row) || "").trim();
      if (!nextValue || !this.selectedProfileOverrideId || !this.effectiveMarketId) {
        return;
      }

      const rowKey = this.getOverrideRowKey(row);
      this.$set(this.overrideBusyByKey, rowKey, true);

      const basePayload = {
        ProfileId: this.selectedProfileOverrideId,
        MarketId: this.effectiveMarketId,
        AttributeId: row.attributeId,
      };

      const actionPromise =
        row.rowType === "attribute"
          ? this.SetProfileAttributeNameOverride({
              ...basePayload,
              OverrideName: nextValue,
            })
          : this.SetProfileAttributeValueNameOverride({
              ...basePayload,
              ValueId: row.valueId,
              OverrideName: nextValue,
            });

      actionPromise
        .then(() => {
          // Clear the draft input after successful save
          this.$set(this.overrideDraftsByKey, rowKey, "");
          this.loadProfileOverrides();
        })
        .finally(() => {
          this.$set(this.overrideBusyByKey, rowKey, false);
        });
    },
    removeOverrideRow(row) {
      if (!this.selectedProfileOverrideId || !this.effectiveMarketId) {
        return;
      }

      const rowKey = this.getOverrideRowKey(row);
      this.$set(this.overrideBusyByKey, rowKey, true);

      const basePayload = {
        ProfileId: this.selectedProfileOverrideId,
        MarketId: this.effectiveMarketId,
        AttributeId: row.attributeId,
      };

      const actionPromise =
        row.rowType === "attribute"
          ? this.RemoveProfileAttributeNameOverride(basePayload)
          : this.RemoveProfileAttributeValueNameOverride({
              ...basePayload,
              ValueId: row.valueId,
            });

      actionPromise
        .then(() => {
          this.$set(this.overrideDraftsByKey, rowKey, "");
          this.loadProfileOverrides();
        })
        .finally(() => {
          this.$set(this.overrideBusyByKey, rowKey, false);
        });
    },
    setDefaultOverrideRow(row) {
      this.removeOverrideRow(row);
    },
    saveOrder() {
      this.orderingAttributes = true;

      this.attributes.forEach((attr, index) => {
        attr.order = index;
      });

      this.OrderAttributes(this.attributes).then(() => {
        this.orderingAttributes = false;
      });
    },
    addAttribute() {
      if (this.$refs.addForm.validate()) {
        this.attributeInCreation = true;

        var newAttribute = {
          name: this.attributeName,
          attributeType: this.type,
        };

        this.CreateAttribute(newAttribute).then(() => {
          this.attributeInCreation = false;
          this.attributeName = null;
          this.type = null;
          this.showAddDialog = false;

          this.$emit("AttributeCreated", newAttribute);
        });
      }
    },
    async updateAttribute() {
      const canEdit = await this.resolveAttributeNameEditability(this.attributeToEdit);
      if (!canEdit) {
        return;
      }

      this.updatingAttribute = true;
      const index = this.attributes.indexOf(this.attributeToEdit);

      var attributeType =
        this.attributeToEdit.type == "Multi"
          ? 3
          : this.attributeToEdit.type == "String"
          ? 1
          : 2;

      var editAttribute = {
        id: this.attributeToEdit.assetAttributeId,
        name: this.attributeName,
        attributeType: attributeType,
      };

      this.RenameAttribute(editAttribute).then(() => {
        this.attributes[index].name = this.attributeName;
        this.showEditDialog = false;
        this.attributeToEdit = {};
        this.updatingAttribute = false;
      });
    },
    deleteItem() {
      this.deletingAttribute = true;
      const index = this.attributes.indexOf(this.attributeToDelete);

      if (this.attributeToDelete.isAttributeCategory) {
        //this will remove the category, the asset attribute and all references to this inside assets
        this.RemoveCategoryAndAssets(
          this.attributeToDelete.attributeCategoryId
        ).then(() => {
          this.attributeToDelete = null;
          this.showDeleteDialog = false;
          this.deletingAttribute = false;

          this.attributes.splice(index, 1);
        });
      } else {
        //this will remove the asset attribute and all references to this inside assets
        this.RemoveAssetAttribute(this.attributeToDelete.assetAttributeId).then(
          () => {
            this.attributeToDelete = null;
            this.showDeleteDialog = false;
            this.deletingAttribute = false;

            this.attributes.splice(index, 1);
          }
        );
      }
    },
    expand(item) {
      this.expanded = [item];
    },
    closeExpansion() {
      this.expanded = [];
    },
  },
  watch: {
    selectedProfileOverrideId() {
      this.profileRowsPage = 1;
      this.loadProfileOverrides();
    },
    effectiveMarketId() {
      this.profileRowsPage = 1;
      this.loadProfileOverrides();
    },
    profileEditorSearch() {
      this.profileRowsPage = 1;
    },
    attributes: {
      handler() {
        this.profileRowsPage = 1;
        this.loadAttributeClassifications();
        this.loadMissingMultiAttributeValues();
        this.mergeScopeEntries(this.attributes || []);
        this.ensureAttributeScopeMapLoaded();
      },
      immediate: true,
    },
  },
};
</script>

<style>
.handle {
  cursor: move !important;
  cursor: -webkit-grabbing !important;
}

.profileOverrideControls {
  gap: 4px;
}

.profileAttributeLabel {
  display: flex;
  align-items: center;
}

.profileValueLabel {
  display: flex;
  align-items: center;
  padding-left: 16px;
}

.profile-toggle-btn {
  width: 16px;
  height: 16px;
  min-width: 16px;
}

.profile-toggle-spacer {
  display: inline-block;
  width: 16px;
  min-width: 16px;
}

.profile-override__container {
  align-items: flex-start;
  width: 100%;
}

.profile-override__input-wrapper {
  flex: 0 0 auto;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.profile-override__chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1 1 auto;
}

.profile-override__default-icon {
  cursor: pointer;
}

.gap-1 {
  gap: 8px;
}

.gap-2 {
  gap: 16px;
}
</style>
